import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  LinkButton,
  MultiSelect,
  PrimaryButton,
  PriceBandIcon,
  SearchField,
  SnackbarStatus,
  StatusBar,
  Toggle,
  Typography,
} from '@weareopenr/component-library';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import SelectableListWithControls from 'src/components/SelectableListWithControls/SelectableListWithControls';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { useVenueCategories } from 'src/hooks/useVenueCategories/useVenueCategories';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useReportTrayDirty } from 'src/contexts/menuTray';
import { useUpdateSectionAvailability } from 'src/hooks/useUpdateSectionAvailability/useUpdateSectionAvailability';
import { getChannelLabel, getPricingChannels } from 'src/helpers/channels/getPricingChannels';
import {
  getSelectedSystemIds,
  getVenueChannelNames,
  isVenueEligibleForChannels,
  NOT_SET_UP_FOR_CHANNELS,
} from 'src/helpers/venueChannelAvailability/venueChannelAvailability';
import {
  isSectionRestricted,
  isSectionVenueRestricted,
  resolveSectionAvailability,
  resolveSectionVenueAvailability,
} from 'src/helpers/sectionAvailability/sectionAvailability';
import { filterVenueOptions, venueCategoriesToSelectOptions } from 'src/utils/venueCategories/mappedVenueCategories';
import { ISection } from 'src/types/menu.types';
import styles from './ManageAvailabilityTrayContent.module.scss';

export interface ManageAvailabilityTrayContentProps {
  menuId: string;
  section: ISection;
  sections: ISection[];
  onClose: () => void;
  openId: number;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const ManageAvailabilityTrayContent = ({
  menuId,
  section,
  sections,
  onClose,
  openId,
  onDirtyChange,
}: ManageAvailabilityTrayContentProps): ReactElement => {
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: externalSystems, isLoading: isChannelsLoading } = useExternalSystems();
  const { data: venues, isLoading: isVenuesLoading } = useVenues();
  const { data: venueCategoriesData, nestedCategories, isLoading: isCategoriesLoading } = useVenueCategories();
  const { mutateAsync: updateAvailability, isLoading: isSubmitting } = useUpdateSectionAvailability(
    menuId,
    section,
    onClose,
  );

  const [isRestricted, setIsRestricted] = useState(
    () => isSectionRestricted(section) || isSectionVenueRestricted(section),
  );
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<string[]>(section.instanceIds ?? []);
  const [selectedVenueIds, setSelectedVenueIds] = useState<string[]>(section.venueIds ?? []);
  const [venueSearchText, setVenueSearchText] = useState('');
  const [filteredCategoryIds, setFilteredCategoryIds] = useState<string[]>([]);
  const debouncedVenueSearch = useDebounce(venueSearchText);

  const initialInstanceIds = section.instanceIds ?? [];
  const initialVenueIds = section.venueIds ?? [];
  const isInitiallyRestricted = isSectionRestricted(section) || isSectionVenueRestricted(section);
  const hasSameIds = (ids: string[], initialIds: string[]) =>
    ids.length === initialIds.length && ids.every((id) => initialIds.includes(id));

  const hasChanges = isRestricted
    ? !isInitiallyRestricted ||
      !hasSameIds(selectedInstanceIds, initialInstanceIds) ||
      !hasSameIds(selectedVenueIds, initialVenueIds)
    : isInitiallyRestricted;

  useReportTrayDirty(hasChanges, onDirtyChange);

  useEffect(() => {
    setIsRestricted(isSectionRestricted(section) || isSectionVenueRestricted(section));
    setSelectedInstanceIds(section.instanceIds ?? []);
    setSelectedVenueIds(section.venueIds ?? []);
    setVenueSearchText('');
    setFilteredCategoryIds([]);
    clearError();
  }, [openId, section, clearError]);

  const parentSection = section.parentSectionId
    ? sections.find((candidate) => candidate.sectionId === section.parentSectionId)
    : undefined;

  const allowedInstanceIds = useMemo(
    () => (parentSection ? resolveSectionAvailability(parentSection, sections).instanceIds : null),
    [parentSection, sections],
  );

  const allowedVenueIds = useMemo(
    () => (parentSection ? resolveSectionVenueAvailability(parentSection, sections).venueIds : null),
    [parentSection, sections],
  );

  const getInheritedRestrictionLabel = (): string => {
    if (allowedInstanceIds && allowedVenueIds) return 'channels and venues';
    if (allowedInstanceIds) return 'channels';
    return 'venues';
  };

  const channelOptions = useMemo(() => {
    const channels = getPricingChannels(externalSystems);
    return channels.map((channel) => ({
      value: channel.extensionInstanceId,
      label: getChannelLabel(channel),
      disabled: !!allowedInstanceIds && !allowedInstanceIds.includes(channel.extensionInstanceId),
    }));
  }, [externalSystems, allowedInstanceIds]);

  const selectedSystemIds = useMemo(
    () => getSelectedSystemIds(selectedInstanceIds, getPricingChannels(externalSystems)),
    [selectedInstanceIds, externalSystems],
  );

  // A venue left selected from before a channel change stops being eligible the moment its
  // channel overlap disappears - the picker should never save a combination the backend would
  // reject with "venue is not set up for the selected channel(s)".
  useEffect(() => {
    if (!venues) return;
    setSelectedVenueIds((currentIds) => {
      const stillEligible = currentIds.filter((id) => {
        const venue = venues.find((candidate) => candidate.venue_id === id);
        return venue ? isVenueEligibleForChannels(venue, selectedSystemIds) : false;
      });
      return stillEligible.length === currentIds.length ? currentIds : stillEligible;
    });
  }, [venues, selectedSystemIds]);

  const categoryOptions = useMemo(() => venueCategoriesToSelectOptions(nestedCategories), [nestedCategories]);

  const allVenueOptions = useMemo(
    () =>
      (venues ?? []).map((venue) => {
        const eligible = isVenueEligibleForChannels(venue, selectedSystemIds);
        const outsideParentScope = !!allowedVenueIds && !allowedVenueIds.includes(venue.venue_id);
        return {
          id: venue.venue_id,
          label: venue.name,
          subtitle: eligible ? getVenueChannelNames(venue, selectedSystemIds).join(', ') : NOT_SET_UP_FOR_CHANNELS,
          disabled: !isRestricted || !eligible || outsideParentScope,
        };
      }),
    [venues, selectedSystemIds, allowedVenueIds, isRestricted],
  );

  const venueOptions = useMemo(
    () =>
      filterVenueOptions(allVenueOptions, debouncedVenueSearch, filteredCategoryIds, venueCategoriesData?.categories),
    [allVenueOptions, debouncedVenueSearch, filteredCategoryIds, venueCategoriesData],
  );

  const selectedVenueOptions = useMemo(
    () => venueOptions.filter((option) => selectedVenueIds.includes(option.id)),
    [venueOptions, selectedVenueIds],
  );

  const handleSave = async () => {
    clearError();
    try {
      await updateAvailability(
        isRestricted
          ? {
              instanceIds: selectedInstanceIds.length > 0 ? selectedInstanceIds : null,
              venueIds: selectedVenueIds.length > 0 ? selectedVenueIds : null,
            }
          : { instanceIds: null, venueIds: null },
      );
    } catch (e) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message:
          e instanceof Error && e.message
            ? e.message
            : 'Something went wrong whilst updating the menu section availability',
      });
    }
  };

  const hasSelection = selectedInstanceIds.length > 0 || selectedVenueIds.length > 0;

  return (
    <div className={styles['content']}>
      <div className={styles['toggle-row']}>
        <Typography weight="semi-bold">Restrict to selected venues and channels</Typography>
        <Toggle
          name="is-restricted"
          ariaLabel="Restrict to selected venues and channels"
          checked={isRestricted}
          onChange={(event) => setIsRestricted(event.target.checked)}
        />
      </div>

      <div className={styles['restricted-section']} aria-disabled={!isRestricted}>
        {parentSection && (allowedInstanceIds || allowedVenueIds) && (
          <div className={styles['inherited-notice']}>
            <StatusBar
              variant="info"
              message={`This sub-section can only use ${getInheritedRestrictionLabel()} that "${parentSection.name}" is already available on.`}
            />
          </div>
        )}

        <div className={styles['multi-select-container']}>
          <Autocomplete
            multiple
            name="channels"
            label="Channels"
            options={channelOptions}
            value={selectedInstanceIds}
            onChange={setSelectedInstanceIds}
            loading={isChannelsLoading}
            disabled={!isRestricted || isChannelsLoading}
            limitChips={1}
          />
        </div>

        <div className={styles['divider']} />

        <div className={styles['venue-filters']}>
          <SearchField
            isDisabled={!isRestricted || isVenuesLoading}
            placeholder="Search for a venue"
            onChange={(event) => setVenueSearchText(event.target.value)}
          />
          <MultiSelect
            Icon={PriceBandIcon}
            name="venue-categories"
            label="Categories"
            className={styles['venue-categories-filter']}
            selected={filteredCategoryIds}
            options={categoryOptions}
            onChange={setFilteredCategoryIds}
            disabled={!isRestricted || isCategoriesLoading}
          />
        </div>

        <SelectableListWithControls
          className={styles['venue-list']}
          options={venueOptions}
          selectedOptionsIds={selectedVenueIds}
          onOptionChange={setSelectedVenueIds}
          scrollContainerClassName={styles['venue-list-scroll']}
          collapsible={{
            selectedOptionsData: selectedVenueOptions,
            isDisabled: !isRestricted,
            isLoading: isVenuesLoading,
          }}
          listDetails={{ title: 'Venues:', isLoading: isVenuesLoading }}
        />
      </div>

      <div className={styles['button-row']}>
        <div className={styles['error-banner']}>
          {errorMessage && (
            <div className={styles['error-banner']}>
              <StatusBar message={errorMessage} variant="error" />
            </div>
          )}
        </div>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <LinkButton type="button" onClick={onClose}>
              Cancel
            </LinkButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              type="button"
              onClick={handleSave}
              loading={isSubmitting}
              disabled={isSubmitting || (isRestricted && !hasSelection)}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export default ManageAvailabilityTrayContent;
